import { Grid, styled } from "@mui/material";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconButton } from "src/components/IconButton/IconButton";
import { getFlightErrorsAction } from "src/redux/actions/flights/FlightScanner.Actions";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { ITimeIntervalLoaded } from "src/redux/reducers/FlightTimelineReducer";
import { IMosaicTabNode } from "src/utils/MosaicTree";

// Error type icons (placeholders)
import { IViolationResult } from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { getQualifications, getViolations } from "src/API/CrewScheduleErrors";
import { ErrorType } from "src/pages/Errors/ErrorsWindow";
import { errorForwardOptions } from "src/pages/Errors/FlightErrorTable";

interface IScanningIconProps {
  errorType: ErrorType;
  state?: "active" | "success" | "error";
  className?: string;
  handleClickLink?: (node?: IMosaicTabNode) => Promise<boolean>;
  isDisabled?: boolean;
  toolTip?: string;
}

const OCScanningIcon = styled("div")(({ theme }) => ({
  display: "flex",
  "& div": { display: "flex" },
  "& button": { borderRadius: 3 },
  "& .icon": {
    backgroundColor: theme.palette.success.light,
    padding: "2px 4px",
    borderRadius: 3,
    "& .emoji": {
      fontSize: 12,
    },
    // Displays error state if error count exceeds 0
    "&.error": {
      backgroundColor: theme.palette.error.main,
    },
    "&.unknown": {
      backgroundColor: theme.palette.grey[600],
    },
    "& .error_count": {
      fontSize: 14,
      fontWeight: 300,
    },
  },
}));

export const ScanningIcon = (props: IScanningIconProps): JSX.Element => {
  const dispatch = useDispatch();

  const [filteredFlightErrorCount, setFilteredFlightErrorCount] = useState<number>(-1);
  const timeIntervalLoaded: ITimeIntervalLoaded = useSelector(
    (state: IRootState) => state.timelineFlights.timeIntervalLoaded
  );
  const showFutureViolationsDefault = useSelector(
    (state: IRootState) => state.UISettings.violations?.showOnlyFutureDefault ?? false
  );
  const isLoadingCrewViolations = useSelector((state: IRootState) => state.crewSchedule.isLoadingCrewViolations);
  const { className, handleClickLink, isDisabled, toolTip, errorType } = props;
  const [APICallMade, setAPICallMade] = useState<boolean>(false);
  // RETRIEVE FLIGHT ERRORS if not already loaded into redux.
  const flightScanner = useSelector((state: IRootState) => state.flightScanner);
  const getFlightErrors = useCallback(() => {
    dispatch(getFlightErrorsAction());
  }, [dispatch]);

  useEffect(() => {
    if (!flightScanner.hasData && errorType === ErrorType.FLIGHT_ERRORS && !APICallMade) {
      getFlightErrors();
      setAPICallMade(true);
    }
  }, [APICallMade, errorType, flightScanner, getFlightErrors]);

  // RETRIEVE VIOLATIONS if not already loaded into redux.
  const violationsMap = useSelector((state: IRootState) => state.crewSchedule.crewViolations);
  const loadedViolations = useSelector((state: IRootState) => state.crewSchedule.loadedViolations);
  const getViolationsAction = useCallback(() => {
    dispatch(getViolations());
  }, [dispatch]);

  useEffect(() => {
    if (!violationsMap && errorType === ErrorType.VIOLATION_ERRORS && !APICallMade) {
      getViolationsAction();
      setAPICallMade(true);
    }
  }, [violationsMap, getViolationsAction, errorType, APICallMade]);

  // RETRIEVE QUALIFICATION ERRORS if not already loaded into redux.
  const crewQualificationsMap = useSelector((state: IRootState) => state.crewSchedule.crewQualifications);
  const getQualificationsAction = useCallback((): void => {
    dispatch(getQualifications());
  }, [dispatch]);

  useEffect(() => {
    if (!crewQualificationsMap && errorType === ErrorType.QUALIFICATION_ERRORS && !APICallMade) {
      getQualificationsAction();
    }
  }, [APICallMade, crewQualificationsMap, errorType, getQualificationsAction]);

  const errorCount: number = useMemo(() => {
    switch (errorType) {
      case ErrorType.FLIGHT_ERRORS: {
        // COUNT ALL FLIGHT ERRORS
        if (!flightScanner.hasData) {
          return -1;
        }
        const { flightErrorsMap } = flightScanner;
        const values = Object.values(flightErrorsMap);

        // Get a reduced count based on the default filter of 24 hours.
        let filteredErrorCount = 0;
        const defaultForwardFilterHours = errorForwardOptions[0].value;
        const now = new Date();
        const filterUntil: number = moment(now).utc(true).add(defaultForwardFilterHours, "hours")?.valueOf();

        let flightErrorCount = 0;
        values.forEach((fltErrors: any) => {
          const filteredErrors = fltErrors.errors.filter(
            (error: any) => new Date(error.actualOut)?.valueOf() <= filterUntil
          );
          filteredErrorCount += filteredErrors.length;
          flightErrorCount += fltErrors.errors.length;
        });
        setFilteredFlightErrorCount(filteredErrorCount);
        return flightErrorCount;
      }
      case ErrorType.VIOLATION_ERRORS: {
        // COUNT ALL VIOLATIONS
        if (!violationsMap || !loadedViolations) {
          return -1;
        }
        // count only violioations with a status of fail
        const viols = new Set([]);
        // compute now once so the badge count matches ViolationErrorTable's filter
        const now = new Date().valueOf();
        for (const crewViolations of Object.values(violationsMap)) {
          for (const yearMonth of Object.values(crewViolations?.violations ?? {})) {
            yearMonth.regulationResults.forEach((violation) => {
              //also account for if we want to show future violations by checking the violation period start against the current time
              if (
                violation.status === "fail" &&
                !violation.noShow &&
                (!showFutureViolationsDefault || violation.period?.start > now)
              )
                viols.add(violation._id);
            });
          }
        }

        return viols.size;
      }
      case ErrorType.QUALIFICATION_ERRORS: {
        // COUNT ALL QUALIFICATIONS
        if (!crewQualificationsMap) {
          return -1;
        }
        // Errors for each crew are mapped in an object so iterate through each one and return the sum of error counts for each crew member
        let qualificationErrorCount = 0;
        const qualificationErrorCounts = Object.values(
          Object.values(crewQualificationsMap).flatMap((val) => Object.values(val.qualifications))
        );
        qualificationErrorCounts.forEach((errorCount) => {
          qualificationErrorCount += errorCount?.length ?? 0;
        });
        // const qualificationTableErrors = Object.values(crewQualificationsMap).flatMap((val) => Object.values(val));
        return qualificationErrorCount;
      }
    }
    return 0;
  }, [errorType, flightScanner, violationsMap, loadedViolations, crewQualificationsMap, showFutureViolationsDefault]);

  const state = errorCount === -1 ? "unknown" : errorCount > 0 ? "error" : "success";

  const getErrorTypeIcon = useMemo(() => {
    switch (errorType) {
      case ErrorType.FLIGHT_ERRORS:
        //<FlightIcon className={`icon_foreground ${state}`} viewBox="-4 -1 32 32" />;
        return (
          <span role="img" className="emoji plane" aria-label="flight errors">
            ✈️
          </span>
        );
      case ErrorType.VIOLATION_ERRORS:
        //return <AssignmentLateIcon className={`icon_foreground ${state}`} viewBox="-4 -1 32 32" />;
        return (
          <span role="img" className="emoji pilot" aria-label="crew violations">
            👨‍✈️
          </span>
        );
      case ErrorType.QUALIFICATION_ERRORS:
        //return <CardMembershipIcon className={`icon_foreground ${state}`} viewBox="-4 -1 32 32" />;
        return (
          <span role="img" className="emoji graduate" aria-label="crew qualification errors">
            🎓
          </span>
        );
    }
    return null;
  }, [errorType]);

  const errorTypeLabel = useMemo(() => {
    const windowTabLabel = (errorType: ErrorType): string => {
      switch (errorType) {
        case ErrorType.FLIGHT_ERRORS:
          return "Flight Error";
        case ErrorType.VIOLATION_ERRORS:
          return "Crew Violation";
        case ErrorType.QUALIFICATION_ERRORS:
          return "Crew Qualification Error";
      }
      return "";
    };
    return windowTabLabel(errorType);
  }, [errorType]);

  // Dispatch open Flight Error window once launchpad is closed.
  const openFlightErrorsWindow = (errorType: ErrorType): void => {
    if (isDisabled) {
      // console.error("Currently disabled action");
      return;
    }

    if (handleClickLink) {
      void handleClickLink().then((launchpadClosed: boolean) => {
        if (launchpadClosed) {
          dispatch(
            openWindow(
              errorTypeLabel + "s",
              "ErrorsWindow",
              [],
              {
                errorType: errorType,
              },
              "context"
            )
          );
        }
      });
    }
  };

  // const flightErrorCount: number =
  //   !!flightScanner && !!flightScanner.flightErrors ? flightScanner.flightErrors.length : 0;
  // <Badge classes={{ root: classes.badge }} badgeContent={flightScanner.flightErrors.length} max={999}>

  const toolTipMessage: string = useMemo(() => {
    return isDisabled && !toolTip
      ? "Currently disabled. Please check back soon!"
      : !timeIntervalLoaded && !toolTip
        ? `Please load ${errorTypeLabel} data first`
        : toolTip
          ? toolTip
          : `${errorTypeLabel} Count ${
              filteredFlightErrorCount > -1 ? `- Count for up to ${errorForwardOptions[0].label} ahead/Total Count` : ""
            }`;
  }, [errorTypeLabel, isDisabled, timeIntervalLoaded, toolTip, filteredFlightErrorCount]);

  return (
    <OCScanningIcon>
      <IconButton
        className={className}
        id="open_scan_results_btn"
        tooltipProps={{ title: toolTipMessage }}
        size="small"
        aria-label={`${errorType} event scanner`}
        disabled={isDisabled || !timeIntervalLoaded}
        onClick={() => openFlightErrorsWindow(errorType)}
      >
        {/* <SvgIcon className="icon"></SvgIcon> */}
        <Grid container className={classNames("icon", isDisabled && "disabled", state)}>
          {getErrorTypeIcon}
          {errorCount > 0 && (
            <div className="error_count">
              {filteredFlightErrorCount > -1 ? `${filteredFlightErrorCount}/` : ""}
              {errorCount}
            </div>
          )}
        </Grid>
      </IconButton>
    </OCScanningIcon>
  );
};
